import { useEffect, useMemo, useState } from 'react';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { PlayerNameButton } from '@/components/shared/PlayerNameButton';
import { Button } from '@/components/shared/Button';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import { useGuildChat } from '@/app/contexts/GuildChatContext';
import { useStickyAutoScroll } from '@/hooks/useStickyAutoScroll';
import { formatDateTime } from '@/utils/date';

function formatChatTime(value: string): string {
  const fallback = formatDateTime(value, 'Time unavailable');
  const timestamp = new Date(value).getTime();

  if (!Number.isFinite(timestamp)) {
    return fallback;
  }

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

export function GuildChatPanel(): JSX.Element | null {
  const { profile, isApproved, isSignedIn, needsOnboarding, hasAnyRole } = useAuth();
  const { state } = useGuildData();
  const { messages, isLoading, error, isCollapsed, isMuted, sendMessage, deleteMessage, clearMessages, toggleCollapsed, toggleMuted } =
    useGuildChat();
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [clearConfirmation, setClearConfirmation] = useState('');
  const canModerateChat = hasAnyRole(['owner', 'admin']);
  const isStaffChatUser = hasAnyRole(['owner', 'admin', 'officer', 'mass_manager']);
  const canAccessGuildChat = isSignedIn && !needsOnboarding && (isApproved || isStaffChatUser);
  const isChatMuted = Boolean(profile?.chatMuted) && !canModerateChat;
  const profilesById = useMemo(() => new Map(state.profiles.map((entry) => [entry.id, entry])), [state.profiles]);

  const messageRows = useMemo(
    () =>
      messages.map((message) => {
        const sender = profilesById.get(message.profileId);

        return {
          ...message,
          sender,
          isOwnMessage: message.profileId === profile?.id,
        };
      }),
    [messages, profile?.id, profilesById],
  );

  const { containerRef, hasDetachedUpdates, pinNextUpdate, scrollToBottom } = useStickyAutoScroll<HTMLDivElement>({
    dependencyKey: `${messageRows.length}:${isCollapsed ? 'collapsed' : 'open'}`,
    enabled: !isCollapsed,
  });

  useEffect(() => {
    if (!isCollapsed) {
      scrollToBottom();
    }
  }, [isCollapsed, scrollToBottom]);

  if (!canAccessGuildChat) {
    return null;
  }

  if (isCollapsed) {
    return (
      <aside className="guild-chat guild-chat--collapsed">
        <Button variant="secondary" onClick={toggleCollapsed}>
          Open Guild Chat
        </Button>
      </aside>
    );
  }

  return (
    <aside className="guild-chat">
      <header className="guild-chat__header">
        <div>
          <p className="guild-chat__eyebrow">GUILD CHAT</p>
          <strong>Guild Coordination</strong>
        </div>
        <div className="guild-chat__actions">
          {canModerateChat ? (
            <Button
              variant="ghost"
              onClick={() => {
                setClearConfirmation('');
                setIsClearDialogOpen(true);
              }}
              disabled={messages.length === 0 || isClearing}
            >
              Clear Chat
            </Button>
          ) : null}
          <Button variant="ghost" onClick={toggleMuted}>
            {isMuted ? 'Unmute' : 'Mute'}
          </Button>
          <Button variant="ghost" onClick={toggleCollapsed}>
            Hide
          </Button>
        </div>
      </header>

      <div ref={containerRef} className="guild-chat__messages">
        {isLoading ? <p className="copy-block">Loading guild chat...</p> : null}
        {!isLoading && error ? <p className="form-footnote form-footnote--error">{error}</p> : null}
        {!isLoading && !error && messageRows.length === 0 ? <p className="copy-block">No guild messages yet.</p> : null}

        {messageRows.map((message) => (
          <article
            key={message.id}
            className={`guild-chat__message ${message.isOwnMessage ? 'guild-chat__message--own' : ''}`}
            id={`guild-chat-message-${message.id}`}
          >
            <div className="guild-chat__message-meta">
              <PlayerNameButton
                profileId={message.profileId}
                primaryLabel={message.sender?.inGameName ?? message.sender?.displayName ?? 'Unknown Member'}
                secondaryLabel={message.sender?.displayName}
                compact
              />
              <div className="guild-chat__message-actions">
                {message.sender?.chatMuted ? <span className="guild-chat__muted-flag">Muted</span> : null}
                <span>{formatChatTime(message.createdAt)}</span>
                {canModerateChat ? (
                  <Button
                    variant="ghost"
                    className="guild-chat__message-delete"
                    onClick={() => {
                      void deleteMessage(message.id).catch((nextError) => {
                        setSendError(nextError instanceof Error ? nextError.message : 'Failed to delete the guild chat message.');
                      });
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
              </div>
            </div>
            <p>{message.content}</p>
          </article>
        ))}
      </div>

      {hasDetachedUpdates ? (
        <Button variant="ghost" className="guild-chat__jump-button" onClick={scrollToBottom}>
          Jump to newest messages
        </Button>
      ) : null}

      {isChatMuted ? (
        <p className="form-footnote form-footnote--error">
          Guild chat is muted for your account{profile?.chatMutedReason ? `: ${profile.chatMutedReason}` : '.'}
        </p>
      ) : null}
      {sendError ? <p className="form-footnote form-footnote--error">{sendError}</p> : null}

      <form
        className="guild-chat__composer"
        onSubmit={(event) => {
          event.preventDefault();

          if (!draft.trim() || isChatMuted || isSending) {
            return;
          }

          setIsSending(true);
          setSendError(null);
          pinNextUpdate();

          void sendMessage(draft)
            .then(() => {
              setDraft('');
            })
            .catch((nextError) => {
              setSendError(nextError instanceof Error ? nextError.message : 'Failed to send the guild chat message.');
            })
            .finally(() => {
              setIsSending(false);
            });
        }}
      >
        <textarea
          className="field__input field__input--textarea guild-chat__input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder="Share calls, regroup info, or quick updates..."
          disabled={isChatMuted || isSending}
        />
        <Button type="submit" fullWidth disabled={isSending || !draft.trim() || isChatMuted}>
          {isSending ? 'Sending...' : 'Send Message'}
        </Button>
      </form>

      <ConfirmationDialog
        open={isClearDialogOpen}
        title="Clear Guild Chat"
        description="Remove the current guild chat history for everyone? This is intended for admin moderation or a clean seasonal reset."
        confirmLabel="Clear Chat"
        confirmVariant="danger"
        confirmationLabel="Type CLEAR to confirm"
        requiredText="CLEAR"
        confirmationValue={clearConfirmation}
        onConfirmationValueChange={setClearConfirmation}
        isProcessing={isClearing}
        onClose={() => {
          if (isClearing) {
            return;
          }

          setClearConfirmation('');
          setIsClearDialogOpen(false);
        }}
        onConfirm={() => {
          setIsClearing(true);
          setSendError(null);

          void clearMessages()
            .then(() => {
              setClearConfirmation('');
              setIsClearDialogOpen(false);
            })
            .catch((nextError) => {
              setSendError(nextError instanceof Error ? nextError.message : 'Failed to clear guild chat.');
            })
            .finally(() => {
              setIsClearing(false);
            });
        }}
      />
    </aside>
  );
}
